import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/eventReports';
import { actions as templateActions } from 'store/template';
import { actions as campaignActions } from 'store/campaign';
import { actions as schemaActions } from 'store/schema';
import { actions as surveyActions } from 'store/surveys';
import { actions as unSubscribeActions } from 'store/unSubscribe';
import { updatePaginatedFilters } from 'store/helpers/tableAndToolbar/actions';
import { TableComponentV2, ToolbarV2, DisplayModal, MultiSelectDropdown } from 'components';
import { excludeProductNames } from 'constants.js';
import filterHelpers from 'store/helpers/filterHelpers';
import moment from 'moment';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import { actions as guestInsightActions } from 'store/guestInsights';
import LicenseCheck from 'LicenseCheck';
import PermissionCheck from 'PermissionsCheck.js';
import { Alert } from '@material-ui/lab';
import { Switch, FormControlLabel } from '@material-ui/core';

const { 
  getPaginatedEventReports,
  setCurrentEvent,
  unsetCurrentEvent,
  getEventReportCsvFile,
  downloadedEventReportCsvFile,
  resetSnackbar,
  updateEventReportFilters,
  resetEventReportFilters,
  updateEventIdentifierFilter
} = actions;
const { getPaginatedTemplates, getTemplateTags, updateCommunicationFilters } = templateActions;
const { getPaginatedCampaigns, updateCampaignFilters } = campaignActions;
const { getEventIdentifierValues } = schemaActions;
const { getPaginatedSubscriptions } = unSubscribeActions; 
const { getCurrentGuestDetail } = guestInsightActions;
const { 
  updateEventIdFilter,
  updateSurveyHashFilter,
  updateSurveyResultsDetails,
  getPaginatedSurveys
} = surveyActions; 

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const FILTER_DROPDOWN_PAGE_SIZE = 10;

const Events = () => {
  const licenses = LicenseCheck();
  const { hasFeatureSurvey } = licenses;
  const permissions = PermissionCheck();
  const { hasPermission } = permissions;
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    onClickEdit
  } = filterHelpers();
  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [emptySubscriptionMessage, setEmptySubscriptionMessage] = React.useState({
    show: false,
    msg: 'No data available.'
  });
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [currentResults, setCurrentResults] = useState({});
  const [emptyResultsMessage, setEmptyResultsMessage] = React.useState({
    show: false,
    msg: 'No survey linked to this event.'
  });
  const filters = useSelector(
    store => store.eventReports.filters
  );
  const isSubscriptionModalLoading = useSelector(store => store.unSubscribe.gettingPaginated);
  const surveyHashFilter = useSelector(store => store.survey.surveyHashFilter);
  const isGettingSurveyResultsLoading = useSelector(store => store.survey.gettingSurveyResults);
  const currentEventId = useSelector(store => store.survey.eventId);
  const surveyResultDetails = useSelector(store => store.survey.surveyResultDetails);
  const templates = useSelector(store => store.eventReports.paginatedList);
  const isLoading = useSelector(store => store.eventReports.gettingPaginated);
  const isTemplateFilterLoading = useSelector(store => store.template.gettingPaginated);
  const isCampaignFilterLoading = useSelector(store => store.campaign.gettingPaginated);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.productNames
  );
  const isProductNamesLoading = useSelector(
    store => store.events.gettingProductNames
  );
  const eventTypeCategories = useSelector(store => store.events.supportedEventTypes.allEventTypeCategoryOptions);
  const paginatedTemplates = useSelector(store => store.template.paginatedList);
  const paginatedCampaigns = useSelector(store => store.campaign.paginatedList);
  const eventIdentifierValues = useSelector(store => store.schema.eventIdentifier.values);
  const unSubscribeList = useSelector(store => store.unSubscribe.paginatedList);

  const gettingFileSucceeded = useSelector(store => store.eventReports.eventReportCsv.gettingFileSucceeded);
  const gettingFile = useSelector(store => store.eventReports.eventReportCsv.gettingFile);
  const isDownloading = useSelector(store => store.eventReports.eventReportCsv.isDownloading);
  const eventReportCsvBlob = useSelector(store => store.eventReports.eventReportCsv.eventReportCsvBlob);
  const showSnackbar = useSelector(store => store.eventReports.showSnackbar);
  const errorMessage = useSelector(store => store.eventReports.error?.message);
  const isGuestInsightLoading = useSelector(store => store.guestInsights.gettingCurrent);
  const transformedGuestDetail = useSelector(store => store.guestInsights.transformedGuestDetail);
  const businessContextId = useSelector(store => store.user.selectedBusinessContext?.businessContextId);
  const contextDetails = useSelector(store => store.user.contextDetails || []);

  const contextLookup = React.useMemo(() => {
    const lookup = {};
    (contextDetails || []).forEach(context => {
      lookup[context.businessContextId] = context.name;
    });
    lookup.Default = lookup.Default || 'ALL';
    return lookup;
  }, [contextDetails]);

  // Template select dropdown state
  const templateFilters = useSelector(store => store.template.filters);
  const templateTags = useSelector(store => store.template.tagList || []);
  const templateTotalCount = useSelector(store => store.template.filters?.totalCount || 0);

  // Campaign select dropdown state
  const campaignFilters = useSelector(store => store.campaign.filters);
  const campaignTotalCount = useSelector(store => store.campaign.filters?.totalCount || 0);

  const [tenantOnly, setTenantOnly] = useState(businessContextId === 'Default');
  const [selectedTemplates, setSelectedTemplates] = useState(filters?.filterPaginatedTemplates || []);
  const [selectedTemplateHierarchy, setSelectedTemplateHierarchy] = useState([]);
  const [selectedTemplateStatus, setSelectedTemplateStatus] = useState([]);
  const [selectedTemplateTags, setSelectedTemplateTags] = useState([]);

  // Campaign filter state
  const [selectedCampaigns, setSelectedCampaigns] = useState(filters?.filterPaginatedCampaigns || []);
  const [selectedCampaignHierarchy, setSelectedCampaignHierarchy] = useState([]);
  const [selectedCampaignStatus, setSelectedCampaignStatus] = useState([]);

  // Hierarchy options for template dropdown
  const templateHierarchyOptions = [
    { value: 'property', label: 'Property Level', searchScope: 'PROPERTY LEVEL' },
    { value: 'tenantOnly', label: 'Tenant Only', searchScope: 'TENANT ONLY' }
  ];

  // Status options for template dropdown
  const templateStatusOptions = [
    { value: 'active', label: 'Active', searchScope: 'ACTIVE' },
    { value: 'archived', label: 'Archived', searchScope: 'ARCHIVED' }
  ];

  // Hierarchy options for campaign dropdown
  const campaignHierarchyOptions = [
    { value: 'property', label: 'Property Level', searchScope: 'PROPERTY LEVEL' },
    { value: 'tenantOnly', label: 'Tenant Only', searchScope: 'TENANT ONLY' }
  ];

  // Status options for campaign dropdown
  const campaignStatusOptions = [
    { value: 'active', label: 'Active', searchScope: 'ACTIVE' },
    { value: 'archived', label: 'Archived', searchScope: 'ARCHIVED' }
  ];

  // Tag options for template dropdown
  const templateTagOptions = (templateTags || []).map(tag => ({
    label: tag,
    value: tag
  }));

  const productNamesLength = productNames?.filter(productName => !excludeProductNames.includes(productName)).length || 0;

  const optionsToFilterProductNames = productNames
  ? productNames.filter(productName => !excludeProductNames.includes(productName)).map(productName => {
      return { name: 'productName', value: productName, checked: false };
    })
  : [];

  const optionsToFilterEventTypeCategories = eventTypeCategories
    ? Array.from(
        new Set(
          eventTypeCategories
            .filter(option => option.type === "EVENT_TYPE_CATEGORY")
            .map(category => category.eventTypeCategory)
        )
      ).map(category => ({
        name: 'eventTypeCategory',
        value: category,
        checked: false
      }))
    : [];

  const optionsToFilterEventIdentifierValues = eventIdentifierValues.map(identifier => {
    return { name: 'eventIdentifierValue', value: identifier, checked: false, regex: '' };
  });

  const getEventIdentifiersList = () => {
    return filters?.filterEventIdentifierValues.filter(option => option.checked).map(option => option.value);
  };

  const getEventIdentifiersMap = () => {
    return filters?.filterEventIdentifierValues?.reduce((acc, option) => {
      acc[option.value] = option.regex;
      return acc;
    }, {}) || {};
  };

  // Sync selected templates with Redux state
  useEffect(() => {
    if (filters?.filterPaginatedTemplates) {
      setSelectedTemplates(filters.filterPaginatedTemplates);
    }
  }, [filters?.filterPaginatedTemplates]);

  // Sync selected campaigns with Redux state
  useEffect(() => {
    if (filters?.filterPaginatedCampaigns) {
      setSelectedCampaigns(filters.filterPaginatedCampaigns);
    }
  }, [filters?.filterPaginatedCampaigns]);

  useEffect(() => {
    if (!errorMessage) {
      setSnackBarInfo({
        show: showSnackbar,
        message: 'Export successful',
        severity: 'success'
      });
    } else {
      setSnackBarInfo({
        show: showSnackbar,
        message: 'Export failed',
        severity: 'error'
      });
    }
  }, [showSnackbar]);

  useEffect(() => {
    if (!gettingFile) {
      if (gettingFileSucceeded && eventReportCsvBlob.type === 'text/csv') {
        const url = window.URL.createObjectURL(new Blob([eventReportCsvBlob]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Export_Report_${new Date().getTime()}.csv`;
        link.click();
      }
      dispatch(downloadedEventReportCsvFile());
    }
  }, [gettingFileSucceeded]);

  useEffect(() => {
    if (currentEventId) {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: false
        }
      });
    }
  }, [currentEventId]);
  
  useEffect(() => {
    if (surveyHashFilter.length === 0) {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: true
        }
      });
      updateSurveyResultsDetails({});
    } else {
      dispatch(getPaginatedSurveys({ force: true, forEvents: true }));
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: false
        }
      });
    }
  }, [surveyHashFilter]);

  useEffect(() => {
    if (Object.keys(surveyResultDetails).length === 0) {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: true
        }
      });
      setCurrentResults({});
    } else {
      setEmptyResultsMessage((prevState) => {
        return {
          ...prevState,
          show: false
        }
      });
      setCurrentResults(surveyResultDetails);
    }
  }, [surveyResultDetails]);

  useEffect(() => {
    if (productNamesLength) {
      dispatch(getEventIdentifierValues({ filteredProductName: filters?.filterProductNames }));
    }
  }, [filters?.filterProductNames, productNamesLength]);

  // Keep tenant scope in sync with selected context and fetch accordingly
  useEffect(() => {
    if (!businessContextId) {
      return;
    }

    if (businessContextId === 'Default') {
      setTenantOnly(true);
      dispatch(updateEventReportFilters({ searchScopes: ['tenantOnly'], pageNo: 0 }));
      dispatch(getPaginatedEventReports({ force: true, searchScopes: ['tenantOnly'] }));
    } else {
      setTenantOnly(false);
      dispatch(updateEventReportFilters({ searchScopes: null, pageNo: 0 }));
      dispatch(getPaginatedEventReports({ force: true, searchScopes: null }));
    }
  }, [businessContextId]);

  useEffect(() => {
    setEmptySubscriptionMessage({
      ...emptySubscriptionMessage,
      show: !Object.keys(unSubscribeList).length > 0
    });
  }, [unSubscribeList]);

  // Event Reports table columns: Email first, then combined Product + Event Type Category
  const tableTitles = [
    {
      id: 'emailAddress',
      label: 'Email Address',
      hasLink: hasPermission('ADM_readUnsubscribe'),
      hasTags: true
    },
    {
      id: 'productAndEventType',
      label: 'Product Name / Event Type Category'
    },
    {
      id: 'createdOn',
      label: 'Created On',
      enableSort: true
    },
    {
      id: 'actionButton',
      label: ''
    }
  ];

  const eventReportRows = (templates || []).map(row => ({
    ...row,
    productAndEventType: `${row.productName || '-'} / ${row.eventTypeCategory || '-'}`,
    tags: tenantOnly && row.businessContextId
      ? [contextLookup[row.businessContextId] || row.businessContextId].filter(Boolean)
      : []
  }));

  const getActionButtons = template => [
    {
      name: 'View Details',
      action: ['ACTION', () => onEdit(template)]
    },
    ...(hasFeatureSurvey() ? [{
      name: 'View Survey Results',
      action: ['ACTION', () => onClickViewSurveyResults(template)]
    }] : []),
    {
      name: 'View Guest Insights',
      action: ['ACTION', () => onClickViewGuestInsights(template)],
      isDisabled: !template.enrichmentData || template.enrichmentData.filter(enrichment => enrichment.enrichmentProduct === 'GPS').length === 0,
      tooltipPlacement: 'left-start',
      tooltipTitle:
        "No Guest Insights available for this event"
    }
  ];

  const onEdit = row => {
    onClickEdit(
      [unsetCurrentEvent(), setCurrentEvent(row)],
      `/events/details`
    );
  };

  const onClickLink = (tableTitle, row) => {
    switch (tableTitle.id) {
      case 'emailAddress':
        dispatch(getPaginatedSubscriptions({ force: true, emailAddress: row.emailAddress, isPopup: true }));
        setIsSubscriptionModalOpen(true);
        break;
    }
  };

  const onClickViewSurveyResults = row => {
    setIsResultsModalOpen(true);
    dispatch(updateSurveyHashFilter([]));
    dispatch(updateSurveyResultsDetails({}));
    dispatch(updateEventIdFilter({ eventId : row.eventId }));
  };

  const onClickViewGuestInsights = row => {
  let gpsEnrichmentProduct = row.enrichmentData.filter(enrichment => enrichment.enrichmentProduct === 'GPS');
    if (gpsEnrichmentProduct.length === 1) {
      window.open(`/gcm/guest-insights/details/${row.enrichmentData[0].enrichmentProductUuid}`, '_blank');
    } else {
      let enrichmentProductUuid = gpsEnrichmentProduct.map((enrichment) => {
        return enrichment.enrichmentProductUuid;
      });
      dispatch(guestInsightActions.updateTransformedGuestDetail({}));
      dispatch(getCurrentGuestDetail({data: enrichmentProductUuid, forEvents: true}));
      setIsGuestModalOpen(true);
    }
  }

  // Template single select handlers
  const fetchPaginatedTemplateOptions = (overrides = {}) => {
    const { rowsPerPage: _rowsPerPage, ...restOverrides } = overrides;
    // Update searchInput in paginatedFilters store for saga to read
    dispatch(
      updatePaginatedFilters({
        searchInput: restOverrides.searchInput || '',
        pageNo: restOverrides.pageNo !== undefined ? restOverrides.pageNo : 0,
        rowsPerPage: FILTER_DROPDOWN_PAGE_SIZE
      })
    );
    dispatch(
      updateCommunicationFilters({
        filterTypes: [],
        filterProductNames: filters?.filterProductNames || [],
        filterProductCategories: [],
        filterTemplates: selectedTemplateHierarchy,
        filterTags: selectedTemplateTags,
        ...restOverrides
      })
    );
    // Build searchScopes based on tenantOnly toggle
    const searchScopes = tenantOnly ? ['tenantOnly'] : null;
    // Build tags from tag filter - only pass if values are selected
    const tagFilters = selectedTemplateTags.length > 0 ? selectedTemplateTags : null;
    dispatch(getPaginatedTemplates({ 
      force: true, 
      isFilter: true,
      excludeListType: true,
      filteredProductName: filters?.filterProductNames,
      searchScopes: searchScopes,
      tags: tagFilters,
      displayActive: selectedTemplateStatus.length === 0 ? null : selectedTemplateStatus.includes('active'),
      displayArchived: selectedTemplateStatus.includes('archived')
    }));
  };

  const handleTemplateDropdownOpen = (initialSearchInput) => {
    dispatch(getTemplateTags());
    fetchPaginatedTemplateOptions({
      pageNo: 0,
      searchInput: (initialSearchInput || '').trim()
    });
  };

  const handleTemplateSearchChange = (searchInput) => {
    fetchPaginatedTemplateOptions({ searchInput, pageNo: 0 });
  };

  const handleTemplatePageChange = (pageNo) => {
    fetchPaginatedTemplateOptions({ pageNo });
  };

  const handleTemplateHierarchyChange = (hierarchyValues) => {
    setSelectedTemplateHierarchy(hierarchyValues);
    dispatch(
      updateCommunicationFilters({
        filterTemplates: hierarchyValues,
        pageNo: 0
      })
    );
    // Build searchScopes based on tenantOnly toggle
    const searchScopes = tenantOnly ? ['tenantOnly'] : null;
    const tagFilters = selectedTemplateTags.length > 0 ? selectedTemplateTags : null;
    dispatch(getPaginatedTemplates({ 
      force: true, 
      isFilter: true,
      excludeListType: true,
      filteredProductName: filters?.filterProductNames,
      searchScopes: searchScopes,
      tags: tagFilters,
      displayActive: selectedTemplateStatus.length === 0 ? null : selectedTemplateStatus.includes('active'),
      displayArchived: selectedTemplateStatus.includes('archived')
    }));
  };

  const handleTemplateStatusChange = (statusValues, options = {}) => {
    setSelectedTemplateStatus(statusValues);
    // Reset pagination to page 0 when filter changes
    const pageNo = options.resetPage ? 0 : templateFilters?.pageNo || 0;
    // Build searchScopes based on tenantOnly toggle
    const searchScopes = tenantOnly ? ['tenantOnly'] : null;
    const tagFilters = selectedTemplateTags.length > 0 ? selectedTemplateTags : null;
    // Update both stores and fetch
    dispatch(
      updatePaginatedFilters({
        searchInput: templateFilters?.searchInput || '',
        pageNo,
        rowsPerPage: FILTER_DROPDOWN_PAGE_SIZE
      })
    );
    dispatch(
      updateCommunicationFilters({
        pageNo
      })
    );
    dispatch(getPaginatedTemplates({ 
      force: true, 
      isFilter: true,
      excludeListType: true,
      filteredProductName: filters?.filterProductNames,
      searchScopes: searchScopes,
      tags: tagFilters,
      displayActive: statusValues.length === 0 ? null : statusValues.includes('active'),
      displayArchived: statusValues.includes('archived')
    }));
  };

  const handleTemplateTagChange = (tagValues, options = {}) => {
    setSelectedTemplateTags(tagValues);
    // Reset pagination to page 0 when filter changes
    const pageNo = options.resetPage ? 0 : templateFilters?.pageNo || 0;
    // Build searchScopes based on tenantOnly toggle
    const searchScopes = tenantOnly ? ['tenantOnly'] : null;
    // Use the new tag values
    const tagFilters = tagValues.length > 0 ? tagValues : null;
    // Update both stores and fetch
    dispatch(
      updatePaginatedFilters({
        searchInput: templateFilters?.searchInput || '',
        pageNo,
        rowsPerPage: FILTER_DROPDOWN_PAGE_SIZE
      })
    );
    dispatch(
      updateCommunicationFilters({
        filterTags: tagValues,
        pageNo
      })
    );
    dispatch(getPaginatedTemplates({ 
      force: true, 
      isFilter: true,
      excludeListType: true,
      filteredProductName: filters?.filterProductNames,
      searchScopes: searchScopes,
      tags: tagFilters,
      displayActive: selectedTemplateStatus.length === 0 ? null : selectedTemplateStatus.includes('active'),
      displayArchived: selectedTemplateStatus.includes('archived')
    }));
  };

  const handleTemplateChange = (selectedTemplateOptions) => {
    setSelectedTemplates(selectedTemplateOptions);
    // Update event reports filter with selected templates
    dispatch(updateEventReportFilters({ 
      filterPaginatedTemplates: selectedTemplateOptions,
      pageNo: 0 
    }));
    dispatch(getPaginatedEventReports({ force: true }));
  };

  const getTemplateOptions = () => {
    return {
      list: (paginatedTemplates || []).map(template => ({
        ...template,
        secondaryText: tenantOnly
          ? `Context: ${contextLookup[template.businessContextId] || template.businessContextId}`
          : ''
      })),
      keyId: 'uuid',
      valueId: 'uuid',
      displayValueId: 'name'
    };
  };

  // Campaign multi-select handlers
  const fetchPaginatedCampaignOptions = (overrides = {}) => {
    const { rowsPerPage: _rowsPerPage, ...restOverrides } = overrides;
    // Update searchInput in paginatedFilters store for saga to read
    dispatch(
      updatePaginatedFilters({
        searchInput: restOverrides.searchInput || '',
        pageNo: restOverrides.pageNo !== undefined ? restOverrides.pageNo : 0,
        rowsPerPage: FILTER_DROPDOWN_PAGE_SIZE
      })
    );
    dispatch(
      updateCampaignFilters({
        filterTypes: [],
        filterProductNames: filters?.filterProductNames || [],
        filterProductCategories: [],
        filterTemplates: selectedCampaignHierarchy,
        ...restOverrides
      })
    );
    // Build searchScopes based on tenantOnly toggle
    const searchScopes = tenantOnly ? ['tenantOnly'] : null;
    dispatch(getPaginatedCampaigns({ 
      force: true, 
      isFilter: true,
      excludeListType: true,
      filteredProductName: filters?.filterProductNames,
      searchScopes: searchScopes,
      displayActive: selectedCampaignStatus.length === 0 ? null : selectedCampaignStatus.includes('active'),
      displayArchived: selectedCampaignStatus.includes('archived')
    }));
  };

  const handleCampaignDropdownOpen = (initialSearchInput) => {
    fetchPaginatedCampaignOptions({
      pageNo: 0,
      searchInput: (initialSearchInput || '').trim()
    });
  };

  const handleCampaignSearchChange = (searchInput) => {
    fetchPaginatedCampaignOptions({ searchInput, pageNo: 0 });
  };

  const handleCampaignPageChange = (pageNo) => {
    fetchPaginatedCampaignOptions({ pageNo });
  };

  const handleCampaignHierarchyChange = (hierarchyValues) => {
    setSelectedCampaignHierarchy(hierarchyValues);
    dispatch(
      updateCampaignFilters({
        filterTemplates: hierarchyValues,
        pageNo: 0
      })
    );
    // Build searchScopes based on tenantOnly toggle
    const searchScopes = tenantOnly ? ['tenantOnly'] : null;
    dispatch(getPaginatedCampaigns({ 
      force: true, 
      isFilter: true,
      excludeListType: true,
      filteredProductName: filters?.filterProductNames,
      searchScopes: searchScopes,
      displayActive: selectedCampaignStatus.length === 0 ? null : selectedCampaignStatus.includes('active'),
      displayArchived: selectedCampaignStatus.includes('archived')
    }));
  };

  const handleCampaignStatusChange = (statusValues, options = {}) => {
    setSelectedCampaignStatus(statusValues);
    // Reset pagination to page 0 when filter changes
    const pageNo = options.resetPage ? 0 : campaignFilters?.pageNo || 0;
    // Build searchScopes based on tenantOnly toggle
    const searchScopes = tenantOnly ? ['tenantOnly'] : null;
    // Update both stores (paginatedFilters first for saga, then campaignFilters for UI)
    dispatch(
      updatePaginatedFilters({
        searchInput: campaignFilters?.searchInput || '',
        pageNo,
        rowsPerPage: FILTER_DROPDOWN_PAGE_SIZE
      })
    );
    dispatch(
      updateCampaignFilters({
        pageNo
      })
    );
    dispatch(getPaginatedCampaigns({ 
      force: true, 
      isFilter: true,
      excludeListType: true,
      filteredProductName: filters?.filterProductNames,
      searchScopes: searchScopes,
      displayActive: statusValues.length === 0 ? null : statusValues.includes('active'),
      displayArchived: statusValues.includes('archived')
    }));
  };

  const handleCampaignChange = (selectedCampaignOptions) => {
    setSelectedCampaigns(selectedCampaignOptions);
    // Update event reports filter with selected campaigns
    dispatch(updateEventReportFilters({ 
      filterPaginatedCampaigns: selectedCampaignOptions,
      pageNo: 0 
    }));
    dispatch(getPaginatedEventReports({ force: true }));
  };

  const getCampaignOptions = () => {
    return {
      list: (paginatedCampaigns || []).map(campaign => ({
        ...campaign,
        secondaryText: tenantOnly
          ? `Context: ${contextLookup[campaign.businessContextId] || campaign.businessContextId}`
          : ''
      })),
      keyId: 'uuid',
      valueId: 'uuid',
      displayValueId: 'name'
    };
  };

  const handleResetFilters = () => {
    setSelectedTemplates([]);
    setSelectedTemplateHierarchy([]);
    setSelectedTemplateStatus([]);
    setSelectedTemplateTags([]);
    setSelectedCampaigns([]);
    setSelectedCampaignHierarchy([]);
    setSelectedCampaignStatus([]);
    dispatch(resetEventReportFilters());
  };

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateEventReportFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterEventTypeCategories.map(option => {
          if (filters?.filterEventTypeCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Event Type Category',
        values: filters?.filterEventTypeCategories,
        onChange: eventTypeCategories =>
          dispatch(updateEventReportFilters({ filterEventTypeCategories: eventTypeCategories, pageNo: 0 }))
      }
    ],
    dateRangeFilters: {
      startDate: {
        title: 'From Date',
        keyName: 'startDate',
        format: 'YYYY-MM-DD',
        value: filters?.filterStartDate,
        maxDate: moment(),
        action: (startDate) => dispatch(updateEventReportFilters({ filterStartDate: startDate, pageNo: 0 })),
      },
      endDate: {
        title: 'To Date',
        keyName: 'endDate',
        format: 'YYYY-MM-DD',
        value: filters?.filterEndDate,
        minDate: filters?.filterStartDate ? filters?.filterStartDate : moment(),
        maxDate: moment(),
        action: (endDate) => dispatch(updateEventReportFilters({ filterEndDate: endDate, pageNo: 0 })),
      },
      disabled: productNamesLength === 0
    },
    regexFilters: [{
      options: optionsToFilterEventIdentifierValues.map(option => {
        if (getEventIdentifiersList()?.includes(option.value)) {
          option.checked = true;
          option.regex = getEventIdentifiersMap()[option.value] || '';
        }
        return option;
      }) || [],
      title: 'Event Identifier values',
      interDependency: filters?.filterProductNames,
      intraDependency: filters?.filterEventIdentifierValues,
      onChange: eventIdentifierValues => dispatch(updateEventIdentifierFilter(eventIdentifierValues)),
    }],
    customFilters: [
      {
        component: (
          <MultiSelectDropdown
            label="Campaigns"
            name="campaignFilter"
            onChange={handleCampaignChange}
            value={selectedCampaigns}
            options={getCampaignOptions()}
            remotePagination
            searchValue={campaignFilters?.searchInput || ''}
            pageNo={campaignFilters?.pageNo || 0}
            rowsPerPage={FILTER_DROPDOWN_PAGE_SIZE}
            rowsPerPageOptions={[]}
            totalCount={campaignTotalCount}
            isLoading={isCampaignFilterLoading}
            statusOptions={campaignStatusOptions}
            selectedStatusValues={selectedCampaignStatus}
            onOpen={handleCampaignDropdownOpen}
            onStatusChange={handleCampaignStatusChange}
            onSearchChange={handleCampaignSearchChange}
            onPageChange={handleCampaignPageChange}
            dataTestId="event-reports-campaign-filter"
          />
        )
      },
      {
        component: (
          <MultiSelectDropdown
            label="Templates"
            name="templateFilter"
            onChange={handleTemplateChange}
            value={selectedTemplates}
            options={getTemplateOptions()}
            remotePagination
            searchValue={templateFilters?.searchInput || ''}
            pageNo={templateFilters?.pageNo || 0}
            rowsPerPage={FILTER_DROPDOWN_PAGE_SIZE}
            rowsPerPageOptions={[]}
            totalCount={templateTotalCount}
            isLoading={isTemplateFilterLoading}
            statusOptions={templateStatusOptions}
            selectedStatusValues={selectedTemplateStatus}
            tagOptions={templateTagOptions}
            selectedTagValues={selectedTemplateTags}
            onOpen={handleTemplateDropdownOpen}
            onStatusChange={handleTemplateStatusChange}
            onTagChange={handleTemplateTagChange}
            onSearchChange={handleTemplateSearchChange}
            onPageChange={handleTemplatePageChange}
            dataTestId="event-reports-template-filter"
          />
        )
      }
    ],
    storeSelector: (store) => store.eventReports.filters,
    resetFilters: handleResetFilters
  };

  const searchOptions = {
    searchPlaceholder: 'Search Email Address',
    searchAction: (payload) => dispatch(updateEventReportFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const getTransformedDetails = row => {
    return {
      'Email Id': row.emailAddress,
      'All Promotional Campaigns': row.allPromotionalCampaignsString,
      'All Transactional Campaigns': row.allTransactionalCampaignsString,
      'Excluded Campaigns': row.campaignUuids
    }
  };

  const handleTenantScopeToggle = (isChecked) => {
    setTenantOnly(isChecked);
    // Clear template selection and filters when changing scope
    setSelectedTemplates([]);
    setSelectedTemplateHierarchy([]);
    setSelectedTemplateStatus([]);
    setSelectedTemplateTags([]);
    // Clear campaign selection and filters when changing scope
    setSelectedCampaigns([]);
    setSelectedCampaignHierarchy([]);
    setSelectedCampaignStatus([]);
    // Clear template and campaign filters, reset searchScopes, and fetch new data
    dispatch(updateEventReportFilters({ 
      searchScopes: isChecked ? ['tenantOnly'] : null,
      filterPaginatedTemplates: [],
      filterPaginatedCampaigns: [],
      pageNo: 0
    }));
    dispatch(getPaginatedEventReports({ force: true }));
  };

  const getButtonsGroup = () => [
    ...(businessContextId === 'Default' ? [{
      name: 'show-reports-across-properties-toggle',
      customNode: (
        <FormControlLabel
          control={
            <Switch
              checked={tenantOnly}
              onChange={(event) => handleTenantScopeToggle(event.target.checked)}
              color="primary"
            />
          }
          label="Show Reports Across Properties"
          style={{ marginLeft: 8, marginRight: 16, whiteSpace: 'nowrap' }}
        />
      )
    }] : []),
    {
      name: 'Export',
      disabled: isDownloading || templates.length === 0 || isLoading,
      action: () =>
        dispatch(getEventReportCsvFile())
    }
  ];

  return (
    <div className={classes.root}>
      { 
        !isProductNamesLoading &&
        productNamesLength === 0 &&
        <Alert severity="info">
          No products are enabled to view event reports.
        </Alert>
      }
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedEventReports({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          onClickLink={onClickLink}
          isLoading={productNamesLength ? isLoading : false}
          tableTitles={tableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={eventReportRows}
          action={getPaginatedEventReports({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateEventReportFilters(payload))}
        />
      </div>
      <DisplayModal
        batch={unSubscribeList.length > 0 ? getTransformedDetails(unSubscribeList[0]) : []}
        title={'Email Preferences'}
        isOpen={isSubscriptionModalOpen}
        setIsOpen={setIsSubscriptionModalOpen}
        emptyMessage={emptySubscriptionMessage}
        isIndentationRequired={true}
        size={'md'}
        showNumberedInnerList={true}
        isLoading={isSubscriptionModalLoading}
      />
      <DisplayModal
        batch={currentResults}
        title={'Linked Surveys'}
        isOpen={isResultsModalOpen}
        setIsOpen={setIsResultsModalOpen}
        emptyMessage={emptyResultsMessage}
        isConversionRequired={true}
        size={'xs'}
        isTable={true}
        isLoading={isGettingSurveyResultsLoading}
      />
      <DisplayModal
        batch={transformedGuestDetail}
        title={'Linked Guest Insights'}
        isOpen={isGuestModalOpen}
        setIsOpen={setIsGuestModalOpen}
        isConversionRequired={false}
        size={'xs'}
        isTable={true}
        isLoading={isGuestInsightLoading}
      />
      {
        <UserMessageSnackbar
          onClose={() => dispatch(resetSnackbar())}
          openSnackbar={snackbarInfo.show}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}>
        </UserMessageSnackbar>
      }
    </div>
  );
};

export default Events;
